import { Label, TextFormField } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import OnboardingFormCoordinator, {
  IOnboardingFormField,
} from 'containers/onboarding/OnboardingFormCoordinator/OnboardingFormCoordinator';
import { Fragment, FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { ClassName } from 'types/ClassName';
import { v_phone, v_require } from 'utils/validations';
import './OwnerOnboardingProfilePage.scss';

export interface IOwnerOnboardingProfilePageProps {
  formData: IProfileData;
  onProfileFormSubmit: (data: IProfileData) => void;
  className?: ClassName;
  disableCaptcha?: boolean;
}

export interface IProfileData {
  firstName: string;
  lastName: string;
  phone: string;
}

const OwnerOnboardingProfilePage: FunctionComponent<IOwnerOnboardingProfilePageProps> = (props) => {
  const { formData, onProfileFormSubmit, className, disableCaptcha = false } = props;

  const [t] = useTranslation();

  function renderFirstNameFormField(form: any, onFocus: () => void, onBlur: () => void) {
    return (
      <Fragment key="firstName">
        <div className="name-field">
          <Label className={'pr-4'}>{t('First name')}</Label>
          <TextFormField
            form={form}
            name="firstName"
            onFocus={onFocus}
            onBlur={onBlur}
            validations={[{ validation: v_require }]}
            className={'pr-4'}
          />
        </div>
      </Fragment>
    );
  }

  function renderLastNameFormField(form: any, onFocus: () => void, onBlur: () => void) {
    return (
      <Fragment key="lastName">
        <div className="name-field">
          <Label>{t('Last name')}</Label>
          <TextFormField
            form={form}
            name="lastName"
            onFocus={onFocus}
            onBlur={onBlur}
            validations={[{ validation: v_require }]}
          />
        </div>
      </Fragment>
    );
  }

  function renderPhoneFormField(form: any, onFocus: () => void, onBlur: () => void) {
    return (
      <Fragment key="phone">
        <Label secondaryLabel={'(xxx-xxx-xxxx)'}>{t('Phone')} #</Label>
        <TextFormField
          type="tel"
          form={form}
          name="phone"
          onFocus={onFocus}
          onBlur={onBlur}
          validations={[{ validation: v_require }, { validation: v_phone }]}
        />
      </Fragment>
    );
  }

  function getFormFields(): IOnboardingFormField[] {
    return [
      {
        panelContent: {
          bodyText: t('Please enter your first name.'),
          headerText: t('What is your first name?'),
          isTitle: false,
        },
        renderField: renderFirstNameFormField,
      },
      {
        panelContent: {
          bodyText: t('Please enter your last name.'),
          headerText: t('What is your last name (surname)?'),
          isTitle: false,
        },
        renderField: renderLastNameFormField,
      },
      {
        panelContent: {
          bodyText: t('What is a good contact phone number?'),
          headerText: t(
            'Please enter your 10-digit phone number. It will only be used if we need to contact you regarding your account.'
          ),
          isTitle: false,
        },
        renderField: renderPhoneFormField,
      },
    ];
  }

  const classes = classNames(
    {
      'owner-onboarding-profile-page': true,
    },
    className
  );

  return (
    <OnboardingFormCoordinator
      defaultHeader={t('Profile')}
      defaultBody={t(`Next, let's get some profile information`)}
      formFields={getFormFields()}
      formData={formData}
      onSubmit={onProfileFormSubmit}
      formButtonLabel={t('Next step')}
      className={classes}
      disableCaptcha={disableCaptcha}
    />
  );
};

export default OwnerOnboardingProfilePage;
